import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  Container,
  Table,
  Button,
  Row,
  Col,
  Alert,
  Pagination,
  Form,
  InputGroup,
} from 'react-bootstrap'
import { PencilSquare, Search } from 'react-bootstrap-icons'

import hospitalsCss from './style.module.scss'
import * as api from './api.client'
import { AdminUser } from './types'

const PAGE_SIZE = 20

const roleLabels: Record<string, string> = {
  ROLE_DOCTOR: 'Médico',
  ROLE_HOSPITAL_ADMIN: 'Admin hospital',
  ROLE_ADMIN: 'Admin',
}

const AdminUsers: FunctionComponent = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [nameFilter, setNameFilter] = useState('')
  const [appliedNameFilter, setAppliedNameFilter] = useState('')
  const [errorMessage, setErrorMessage] = useState({ show: false, message: '' })

  const loadUsers = useCallback(async (pageNumber: number, name: string) => {
    try {
      const result = await api.getUsers(pageNumber, PAGE_SIZE, name)
      setUsers(result.content)
      setPage(result.page)
      setTotalPages(result.totalPages)
      setAppliedNameFilter(name)
      setErrorMessage({ show: false, message: '' })
    } catch (error) {
      setErrorMessage({
        show: true,
        message: 'Erro ao buscar os usuários.',
      })
    }
  }, [])

  useEffect(() => {
    loadUsers(0, '')
  }, [loadUsers])

  const searchByName = (event: React.FormEvent) => {
    event.preventDefault()
    loadUsers(0, nameFilter.trim())
  }

  return (
    <Container fluid className={hospitalsCss.container}>
      {errorMessage.show && (
        <Alert variant="danger" style={{ marginTop: 20 }}>
          {errorMessage.message}
        </Alert>
      )}

      <Row className={hospitalsCss.pageHeader}>
        <Col>
          <h3 className={hospitalsCss.title}>Usuários</h3>
        </Col>
      </Row>

      <Form onSubmit={searchByName} className="mb-3">
        <InputGroup style={{ maxWidth: 420 }}>
          <Form.Control
            type="text"
            placeholder="Buscar por nome"
            value={nameFilter}
            onChange={(event) => setNameFilter(event.currentTarget.value)}
          />
          <Button type="submit" variant="outline-primary">
            <Search /> Buscar
          </Button>
        </InputGroup>
      </Form>

      <div className={hospitalsCss.tableScrollArea}>
        <Table striped hover responsive="sm" className={hospitalsCss.listTable}>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Perfil</th>
              <th>Exibir BI</th>
              <th className={hospitalsCss.tableActions}></th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>{roleLabels[user.role] || user.role}</td>
                <td>{user.displayBi ? 'Sim' : 'Não'}</td>
                <td>
                  <Button
                    className={hospitalsCss.actionButtons}
                    variant="outline-primary"
                    size="sm"
                    onClick={() => navigate(`/users/${user.id}`)}
                  >
                    <PencilSquare size={16} /> <span>Editar</span>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <Pagination className="mt-3 justify-content-center">
          <Pagination.Prev
            disabled={page === 0}
            onClick={() => loadUsers(page - 1, appliedNameFilter)}
          />
          {Array.from({ length: totalPages }, (_, index) => (
            <Pagination.Item
              key={index}
              active={index === page}
              onClick={() => loadUsers(index, appliedNameFilter)}
            >
              {index + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            disabled={page >= totalPages - 1}
            onClick={() => loadUsers(page + 1, appliedNameFilter)}
          />
        </Pagination>
      )}
    </Container>
  )
}

export default AdminUsers
